import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import SitePicker from './SitePicker';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureSpatialPage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [packages, setPackages] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState('');
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    if (!selectedSite?.id) return;
    setLoading(true); setError('');
    Promise.all([
      fetch(`/v1/flux/sites/${selectedSite.id}/data-packages`, { headers: bearerHeaders() }).then(r => r.json()),
      fetch(`/v1/flux/sites/${selectedSite.id}/model-runs`, { headers: bearerHeaders() }).then(r => r.json()),
    ])
      .then(([pkg, run]) => {
        setPackages(pkg.packages || []);
        setRuns(run.runs || []);
        if (!selectedPkg && pkg.packages?.length) setSelectedPkg(pkg.packages[0].id);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.id]);

  useEffect(() => { reload(); }, [reload]);

  const runModel = useCallback(async () => {
    if (!selectedSite?.id) return;
    setRunning(true); setError('');
    try {
      const r = await fetch(`/v1/flux/sites/${selectedSite.id}/model-run`, {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({ data_package_id: selectedPkg || null }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      reload();
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  }, [selectedSite?.id, selectedPkg, reload]);

  if (!selectedProject) return <EmptyState message="Pick a project from the header." />;
  if (!selectedSite) return (
    <SitePicker
      project={selectedProject}
      pageTitle="Spatial Model"
      pageDescription="Run the Haifa spatial model on a data package. Returns a 10×10 m per-pixel NEE raster."
      onPickSite={setSelectedSite}
    />
  );

  const latestRun = runs[0];
  const latestSummary = latestRun?.summary_json
    ? (typeof latestRun.summary_json === 'string' ? JSON.parse(latestRun.summary_json) : latestRun.summary_json)
    : null;

  return (
    <>
      <Helmet><title>Spatial Model | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">{selectedProject.name} / {selectedSite.name}</Typography>
            <Typography variant="h4">Spatial Model</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Send the assembled data package to the Haifa spatial model. Returns a 10×10 m per-pixel NEE raster
              and tightens the estimate to ±15–30%.
            </Typography>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {/* Latest run summary */}
          {latestRun && (
            <Box sx={{ p: 2.5, borderRadius: 1.5, bgcolor: 'success.lighter' }}>
              <Typography variant="overline" color="success.dark">Latest model run</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  {Math.round(latestRun.total_nee_tco2_yr)} tCO₂/yr
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ±{latestRun.uncertainty_pct}% — {latestRun.model_version || 'unknown version'}
                </Typography>
              </Box>
              {latestSummary?.comparison_to_tier1 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  vs Tier 1 baseline ({Math.round(latestSummary.comparison_to_tier1.tier1)} tCO₂/yr):
                  <Chip
                    size="small"
                    label={`${latestSummary.comparison_to_tier1.delta_pct >= 0 ? '+' : ''}${latestSummary.comparison_to_tier1.delta_pct}%`}
                    color={latestSummary.comparison_to_tier1.delta_pct >= 0 ? 'success' : 'warning'}
                    sx={{ ml: 1, fontWeight: 600 }}
                  />
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {latestSummary?.pixel_count?.toLocaleString()} pixels @ {latestSummary?.pixel_resolution_m}m · run {new Date(latestRun.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* Run controls */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Run new spatial model</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                select size="small" label="Data package"
                value={selectedPkg} onChange={e => setSelectedPkg(e.target.value)}
                sx={{ minWidth: 280 }}
                disabled={!packages.length}
              >
                {packages.length === 0 && <MenuItem value="">— none yet, assemble one in Monitoring</MenuItem>}
                {packages.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.period_start} → {p.period_end} · {new Date(p.created_at).toLocaleDateString()}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:play-circle-fill" />}
                onClick={runModel}
                disabled={running || !packages.length}
              >
                {running ? 'Running model (~2s)…' : 'Run Haifa model'}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Phase 0: Haifa API is mocked — returns the bundled demo raster instantly. Real model integration in Phase 2.
            </Typography>
          </Box>

          <Divider />

          {/* Run history */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Run history ({runs.length})</Typography>
            {loading ? <CircularProgress size={24} /> : (
              <Stack spacing={1}>
                {runs.map(run => <RunRow key={run.id} run={run} />)}
                {runs.length === 0 && (
                  <Alert severity="info">No model runs yet. Pick a data package and click "Run Haifa model".</Alert>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </Container>
    </>
  );
}

function RunRow({ run }) {
  const summary = typeof run.summary_json === 'string' ? JSON.parse(run.summary_json) : run.summary_json;
  return (
    <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {Math.round(run.total_nee_tco2_yr)} tCO₂/yr <Typography component="span" variant="caption" color="text.secondary">±{run.uncertainty_pct}%</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {run.model_version} · {summary?.pixel_count?.toLocaleString()} pixels · {new Date(run.created_at).toLocaleString()}
          </Typography>
        </Box>
        {summary?.comparison_to_tier1 && (
          <Chip
            size="small"
            label={`${summary.comparison_to_tier1.delta_pct >= 0 ? '+' : ''}${summary.comparison_to_tier1.delta_pct}% vs Tier 1`}
            color={summary.comparison_to_tier1.delta_pct >= 0 ? 'success' : 'warning'}
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Spatial Model</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}
